import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';
import { to12h } from '../../utils/time';

const fmt = n => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const fmtDateShort = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
const fmtDateLong  = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '';
const toDS = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const todayStr = new Date().toISOString().split('T')[0];

const STATUS_LABEL = {
  confirmed: 'Confirmada', attended: 'Atendida',
  not_attended: 'No asistió', cancelled: 'Cancelada', rescheduled: 'Reagendada',
};

// Monday of a given date's week
const getMonday = d => {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const m = new Date(d); m.setDate(d.getDate() + diff); return m;
};

// ─── SERVICE ICON (emoji by keyword) ──────────────────────────────────────────
const svcIcon = name => {
  const n = (name || '').toLowerCase();
  if (n.includes('barba') || n.includes('beard')) return '🧔';
  if (n.includes('color') || n.includes('tinte')) return '🎨';
  if (n.includes('tratamiento') || n.includes('capilar')) return '💊';
  if (n.includes('afeit')) return '🪒';
  if (n.includes('corte') && n.includes('barba')) return '✂️';
  return '✂️';
};

// ─── STEP INDICATOR ────────────────────────────────────────────────────────────
// Todos los pasos con su índice lógico fijo
const ALL_STEPS = [
  { label: 'Servicio',     idx: 0 },
  { label: 'Sucursal',    idx: 1 },
  { label: 'Barbero',     idx: 2 },
  { label: 'Fecha y hora', idx: 3 },
  { label: 'Datos',       idx: 4 },
  { label: 'Confirmación', idx: 5 },
];

function StepBar({ step, noBranches, noWorkers, onGoTo }) {
  // Solo mostrar los pasos que aplican a este negocio
  const visible = ALL_STEPS.filter(s =>
    !(noBranches && s.idx === 1) && !(noWorkers && s.idx === 2)
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, margin: '0 0 28px', overflowX: 'auto', padding: '4px 0' }}>
      {visible.map(({ label, idx }, order) => {
        const done      = idx < step;
        const active    = idx === step;
        const clickable = done && onGoTo;
        return (
          <React.Fragment key={idx}>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 52, cursor: clickable ? 'pointer' : 'default' }}
              onClick={() => clickable && onGoTo(idx)}
              title={clickable ? `Volver a ${label}` : undefined}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: done ? '#10b981' : active ? '#2563eb' : '#e2e8f0',
                color: done || active ? '#fff' : '#94a3b8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '0.82rem', transition: 'all 0.2s',
                boxShadow: clickable ? '0 0 0 2px rgba(16,185,129,0.25)' : 'none',
              }}>
                {done ? '✓' : order + 1}
              </div>
              <span style={{
                fontSize: '0.65rem', fontWeight: active ? 700 : 500,
                color: active ? '#2563eb' : done ? '#10b981' : '#94a3b8',
                whiteSpace: 'nowrap',
                textDecoration: clickable ? 'underline dotted' : 'none',
              }}>
                {label}
              </span>
            </div>
            {order < visible.length - 1 && (
              <div style={{ width: 28, height: 2, background: idx < step ? '#10b981' : '#e2e8f0', margin: '0 2px 18px', flexShrink: 0, transition: 'all 0.2s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── SUMMARY SIDEBAR ───────────────────────────────────────────────────────────
function SummarySidebar({ booking, step, onChangeStep }) {
  const svc = booking.service;
  const wkr = booking.worker;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, position: 'sticky', top: 20 }}>
      <h3 style={{ margin: '0 0 20px', fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>Resumen de tu cita</h3>

      {/* Servicio */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Servicio</div>
        {svc ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#0f172a' }}>{svc.nombre || svc.name}</div>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                ⏱ {svc.duracion_minutos || svc.duration_minutes} min
              </div>
            </div>
            <div style={{ fontWeight: 700, color: '#2563eb', fontSize: '0.9rem' }}>{fmt(svc.precio || svc.price)}</div>
          </div>
        ) : (
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>Sin seleccionar</div>
        )}
      </div>

      <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />

      {/* Sucursal */}
      {booking.branch && (
        <>
          <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sucursal</div>
              {step > 1 && <button onClick={() => onChangeStep(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#2563eb', fontWeight: 600, padding: 0 }}>Cambiar</button>}
            </div>
            <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>
              🏪 {booking.branch.nombre}
            </div>
            {booking.branch.direccion && (
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>📍 {booking.branch.direccion}</div>
            )}
          </div>
        </>
      )}

      {/* Barbero */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Barbero</div>
          {step > 2 && <button onClick={() => onChangeStep(2)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#2563eb', fontWeight: 600, padding: 0 }}>Cambiar</button>}
        </div>
        {wkr ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {wkr.url_foto
              ? <img src={wkr.url_foto} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
              : <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>✂️</div>
            }
            <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>{wkr.nombre || wkr.name}</span>
          </div>
        ) : step > 2 ? (
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Cualquier disponible</div>
        ) : (
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>Sin seleccionar</div>
        )}
      </div>

      <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />

      {/* Fecha y hora */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Fecha y hora</div>
          {step > 3 && booking.date && <button onClick={() => onChangeStep(3)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#2563eb', fontWeight: 600, padding: 0 }}>Cambiar</button>}
        </div>
        {booking.date && booking.time ? (
          <div style={{ fontSize: '0.85rem', color: '#0f172a', fontWeight: 500 }}>
            📅 {fmtDateShort(booking.date)} · {to12h(booking.time)}
          </div>
        ) : (
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>Sin seleccionar</div>
        )}
      </div>

      {svc && (
        <>
          <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>TOTAL</span>
            <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#10b981' }}>{fmt(svc.precio || svc.price)}</span>
          </div>
        </>
      )}

      {/* Benefits */}
      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[
          ['📅','Confirmación inmediata','Recibirás la confirmación de tu cita al instante.'],
          ['🔔','Recordatorio de cita','Te enviaremos un recordatorio antes de tu cita.'],
          ['↩','Cambios fáciles','Puedes reagendar o cancelar tu cita fácilmente.'],
        ].map(([icon, title, desc]) => (
          <div key={title} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>{icon}</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f172a' }}>{title}</div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 1 }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════ MAIN ══ */
export default function BookingPage() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [workers, setWorkers]   = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [mode, setMode]         = useState('home'); // 'home' | 'book' | 'history'

  useEffect(() => {
    api.get(`/businesses/slug/${slug}`)
      .then(r => {
        setBusiness(r.data);
        return Promise.all([
          api.get(`/services/public/${r.data.id}`),
          api.get(`/workers/public/${r.data.id}`),
          api.get(`/branches/public/${r.data.id}`),
        ]);
      })
      .then(([s, w, b]) => { setServices(s.data); setWorkers(w.data); setBranches(b.data); })
      .catch(() => setError('Barbería no encontrada o link inválido'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div className="spinner" />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center', padding: 48 }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>✂️</div>
        <h3 style={{ color: '#ef4444' }}>{error}</h3>
        <p style={{ color: '#94a3b8', marginTop: 8 }}>Verifica que el link sea correcto.</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {mode === 'home' && <HomeScreen business={business} onBook={() => setMode('book')} onHistory={() => setMode('history')} />}
      {mode === 'book' && <BookingFlow business={business} services={services} workers={workers} branches={branches} slug={slug} onBack={() => setMode('home')} />}
      {mode === 'history' && <HistoryScreen business={business} workers={workers} slug={slug} onBack={() => setMode('home')} />}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ HOME ════ */
function HomeScreen({ business, onBook, onHistory }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#f8fafc' }}>
      <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
        {/* El logo se muestra con su propia forma y proporción: sin recuadro de
            fondo y sin recortes. Solo cuando no hay logo se usa la placa oscura. */}
        {business?.url_logo ? (
          <img
            src={business.url_logo}
            alt={business?.nombre || business?.name || 'Logo'}
            style={{
              maxWidth: 160, maxHeight: 160, width: 'auto', height: 'auto',
              objectFit: 'contain', borderRadius: 16,
              display: 'block', margin: '0 auto 20px',
            }}
          />
        ) : (
          <div style={{ width: 72, height: 72, borderRadius: 20, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, margin: '0 auto 20px' }}>
            💈
          </div>
        )}
        <h1 style={{ fontWeight: 800, fontSize: '1.6rem', color: '#0f172a', marginBottom: 6 }}>{business?.nombre || business?.name}</h1>
        {business?.direccion && <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 32 }}>📍 {business.direccion}</p>}

        <button onClick={onBook} style={{
          width: '100%', padding: '16px', borderRadius: 12, border: 'none',
          background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', marginBottom: 12,
        }}>
          ✂️ Agendar mi cita
        </button>
        <button onClick={onHistory} style={{
          width: '100%', padding: '14px', borderRadius: 12,
          border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
        }}>
          📋 Ver mis citas
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ BOOKING FLOW ═ */
function BookingFlow({ business, services, workers, branches, slug, onBack }) {
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState({ client_name: '', client_phone: '', service: null, branch: null, worker: null, date: '', time: '' });
  const [slots, setSlots]         = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsMsg, setSlotsMsg]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]           = useState(null);
  const [error, setError]         = useState('');

  // Availability
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [availDates, setAvailDates]   = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [dayMsg, setDayMsg]           = useState(''); // aviso al tocar un día no habilitado

  // Aviso temporal cuando se intenta elegir un día no disponible
  const avisarDiaNoDisponible = (ds, esPasado) => {
    const fecha = new Date(ds + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });
    setDayMsg(esPasado
      ? `El ${fecha} ya pasó. Elige una fecha de hoy en adelante.`
      : `El ${fecha} no está disponible para agendar. Por favor selecciona otro día.`);
  };

  // El aviso desaparece solo a los 4 segundos
  useEffect(() => {
    if (!dayMsg) return;
    const t = setTimeout(() => setDayMsg(''), 4000);
    return () => clearTimeout(t);
  }, [dayMsg]);

  // Carga la disponibilidad de TODOS los meses que abarca la semana visible.
  // Una semana puede cruzar dos meses (ej: lun 27 jul – dom 2 ago); antes solo se
  // consultaba el mes del lunes, así que los días del mes siguiente salían
  // siempre deshabilitados aunque el negocio sí atendiera.
  const fetchAvailability = useCallback(async (weekStartDate, workerId, serviceId, branchId) => {
    if (!serviceId) return;
    setLoadingAvail(true); setAvailDates(null);
    try {
      const meses = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStartDate);
        d.setDate(weekStartDate.getDate() + i);
        const clave = `${d.getFullYear()}-${d.getMonth()}`;
        if (!meses.some(m => m.clave === clave)) {
          meses.push({ clave, year: d.getFullYear(), month: d.getMonth() });
        }
      }
      const resultados = await Promise.all(meses.map(m =>
        api.get('/appointments/availability/month', {
          params: {
            business_slug: slug, worker_id: workerId || '',
            year: m.year, month: m.month + 1,
            service_id: serviceId, branch_id: branchId || '',
          }
        }).then(r => r.data.available || []).catch(() => [])
      ));
      setAvailDates(resultados.flat());
    } catch { setAvailDates([]); }
    finally { setLoadingAvail(false); }
  }, [slug]);

  const fetchSlots = useCallback(async (workerId, date, serviceId, branchId) => {
    setSlotsLoading(true); setSlotsMsg('');
    try {
      const res = await api.get('/appointments/availability', {
        params: { business_slug: slug, worker_id: workerId || '', date, service_id: serviceId, branch_id: branchId || '' }
      });
      setSlots(res.data.slots || []);
      if (!(res.data.slots || []).length && res.data.message) setSlotsMsg(res.data.message);
    } catch { setSlots([]); setSlotsMsg('Error consultando disponibilidad.'); }
    finally { setSlotsLoading(false); }
  }, [slug]);

  // When entering step 3 (fecha), load availability for current week
  useEffect(() => {
    if (step === 3 && booking.service) {
      fetchAvailability(weekStart, booking.worker?.id, booking.service?.id, booking.branch?.id);
    }
  }, [step]); // eslint-disable-line

  const goWeek = dir => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d);
    setBooking(b => ({ ...b, date: '', time: '' }));
    setSlots([]);
    setDayMsg('');
    // Siempre se recarga: aunque el lunes siga en el mismo mes, la semana nueva
    // puede abarcar días de otro mes.
    fetchAvailability(d, booking.worker?.id, booking.service?.id, booking.branch?.id);
  };

  const selectDay = ds => {
    setBooking(b => ({ ...b, date: ds, time: '' }));
    setSlots([]);
    fetchSlots(booking.worker?.id, ds, booking.service?.id, booking.branch?.id);
  };

  const normalizePhone = phone => {
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('57')) return digits;
    return '57' + digits;
  };

  const handleConfirm = async () => {
    setSubmitting(true); setError('');
    try {
      const clientRes = await api.post('/clients', { business_id: business.id, name: booking.client_name, phone: normalizePhone(booking.client_phone) });
      await api.post('/appointments/public/book', {
        business_id: business.id, client_id: clientRes.data.id,
        worker_id: booking.worker?.id || null, service_id: booking.service.id,
        appointment_date: booking.date, start_time: booking.time,
      });
      setDone({ ...booking });
    } catch (err) { setError(err.response?.data?.error || 'Error al agendar. Intenta de nuevo.'); }
    finally { setSubmitting(false); }
  };

  // ── SUCCESS ──
  if (done) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38, margin: '0 auto 20px' }}>✅</div>
          <h2 style={{ fontWeight: 800, fontSize: '1.5rem', color: '#0f172a', marginBottom: 8 }}>¡Cita confirmada!</h2>
          <p style={{ color: '#64748b', marginBottom: 28 }}>Te esperamos, {done.client_name.split(' ')[0]}.</p>
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24, textAlign: 'left' }}>
            {[
              ['✂️ Servicio', done.service?.nombre || done.service?.name],
              ['👤 Barbero', done.worker?.nombre || done.worker?.name || 'Cualquier disponible'],
              ['📅 Fecha', fmtDateLong(done.date)],
              ['🕐 Hora', to12h(done.time)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.88rem' }}>
                <span style={{ color: '#64748b' }}>{k}</span>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>{v}</span>
              </div>
            ))}
          </div>
          <button onClick={() => { setDone(null); setStep(0); setBooking({ client_name: '', client_phone: '', service: null, branch: null, worker: null, date: '', time: '' }); }}
            style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, cursor: 'pointer', marginBottom: 10, fontSize: '0.95rem' }}>
            Agendar otra cita
          </button>
          <button onClick={onBack} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}>
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // ── 7 DAYS OF CURRENT WEEK ──
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d;
  });
  const dayNames = ['LUN','MAR','MIÉ','JUE','VIE','SÁB','DOM'];

  // Sucursales: sólo el paso de selección se muestra si hay sucursales activas
  const noBranches = branches.length === 0;
  // Workers filtrados por sucursal seleccionada (o todos si no hay sucursales)
  const branchWorkers = booking.branch
    ? workers.filter(w => String(w.sucursal_id) === String(booking.branch.id))
    : workers;
  const noWorkers = branchWorkers.length === 0;
  // Con un solo barbero activo no tiene sentido ofrecer "Cualquiera disponible":
  // se muestra unicamente ese barbero y se toma su horario.
  const unSoloBarbero = branchWorkers.length === 1;

  // Si hay un solo barbero, queda seleccionado automaticamente para que la
  // disponibilidad se calcule con SU agenda y no con la del negocio completo.
  useEffect(() => {
    if (unSoloBarbero && booking.worker?.id !== branchWorkers[0].id) {
      setBooking(b => ({ ...b, worker: branchWorkers[0], date: '', time: '' }));
    }
  }, [unSoloBarbero, branchWorkers, booking.worker]); // eslint-disable-line

  const canContinue = () => {
    if (step === 0) return !!booking.service;
    if (step === 1) return !!booking.branch;      // Sucursal obligatoria
    if (step === 2) return true;                  // barbero opcional
    if (step === 3) return !!booking.date && !!booking.time;
    if (step === 4) return booking.client_name.trim() && booking.client_phone.trim();
    return false;
  };

  const nextStep = () => {
    let next = step + 1;
    if (next === 1 && noBranches) next++;   // saltar Sucursal
    if (next === 2 && noWorkers)  next++;   // saltar Barbero
    if (next === 3 && booking.service) {    // entrar a Fecha y hora
      fetchAvailability(weekStart, booking.worker?.id, booking.service?.id, booking.branch?.id);
    }
    setStep(next);
  };

  const prevStep = () => {
    let prev = step - 1;
    if (prev === 2 && noWorkers)  prev--;   // saltar Barbero al retroceder
    if (prev === 1 && noBranches) prev--;   // saltar Sucursal al retroceder
    setStep(prev);
  };

  // Navegar a un paso completado — limpia selecciones posteriores al destino
  const goToStep = (target) => {
    if (target >= step) return;
    setBooking(b => ({
      ...b,
      ...(target <= 0 ? { service: null } : {}),
      ...(target <= 1 ? { branch: null } : {}),
      ...(target <= 2 ? { worker: null } : {}),
      ...(target <= 3 ? { date: '', time: '' } : {}),
    }));
    setStep(target);
  };

  const mainContent = (
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* ── STEP 0: SERVICIO ── */}
      {step === 0 && (
        <div>
          <h2 style={{ fontWeight: 800, fontSize: '1.3rem', color: '#0f172a', marginBottom: 4 }}>Selecciona el servicio</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 24 }}>Elige el servicio que deseas realizarte.</p>
          {services.length === 0 && <div style={{ padding: 20, background: '#fefce8', borderRadius: 12, color: '#a16207', fontSize: '0.88rem' }}>Esta barbería no tiene servicios disponibles.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {services.filter(s => s.is_active !== false).map(s => {
              const selected = booking.service?.id === s.id;
              return (
                <div key={s.id} onClick={() => setBooking(b => ({ ...b, service: s }))} style={{
                  background: '#fff', border: `2px solid ${selected ? '#2563eb' : '#e2e8f0'}`,
                  borderRadius: 14, padding: 18, cursor: 'pointer', transition: 'all 0.15s', position: 'relative',
                  boxShadow: selected ? '0 0 0 4px rgba(37,99,235,0.1)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  {selected && (
                    <div style={{ position: 'absolute', top: 14, right: 14, width: 22, height: 22, borderRadius: '50%', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</div>
                  )}
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: selected ? '#eff6ff' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 12 }}>
                    {svcIcon(s.nombre || s.name)}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', marginBottom: 4 }}>{s.nombre || s.name}</div>
                  {(s.descripcion || s.description) && (
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 10, lineHeight: 1.4 }}>{s.descripcion || s.description}</div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>{fmt(s.precio || s.price)}</span>
                    <span style={{ fontSize: '0.75rem', color: selected ? '#2563eb' : '#94a3b8', fontWeight: 600 }}>
                      {s.duracion_minutos || s.duration_minutes} min
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── STEP 1: SUCURSAL ── */}
      {step === 1 && (
        <div>
          <h2 style={{ fontWeight: 800, fontSize: '1.3rem', color: '#0f172a', marginBottom: 4 }}>Elige la sucursal</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 24 }}>Selecciona la sede donde quieres tu cita.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {branches.map(br => {
              const selected = booking.branch?.id === br.id;
              return (
                <div key={br.id}
                  onClick={() => setBooking(b => ({ ...b, branch: br, worker: null }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px',
                    background: '#fff', border: `2px solid ${selected ? '#2563eb' : '#e2e8f0'}`,
                    borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s',
                    boxShadow: selected ? '0 0 0 4px rgba(37,99,235,0.1)' : '0 1px 3px rgba(0,0,0,0.06)',
                  }}>
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: selected ? '#eff6ff' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>🏪</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>{br.nombre}</div>
                    {br.direccion && <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 3 }}>📍 {br.direccion}</div>}
                    {br.telefono  && <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 1  }}>📞 {br.telefono}</div>}
                  </div>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${selected ? '#2563eb' : '#d1d5db'}`, background: selected ? '#2563eb' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── STEP 2: BARBERO ── */}
      {step === 2 && (
        <div>
          <h2 style={{ fontWeight: 800, fontSize: '1.3rem', color: '#0f172a', marginBottom: 4 }}>Elige tu barbero</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 24 }}>
            {unSoloBarbero
              ? 'Tu cita será atendida por este profesional.'
              : booking.branch ? `Barberos disponibles en ${booking.branch.nombre}.` : 'Selecciona el profesional de tu preferencia.'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Cualquier disponible — solo si hay mas de un barbero */}
            {!unSoloBarbero && (() => {
              const selected = !booking.worker;
              return (
                <div onClick={() => setBooking(b => ({ ...b, worker: null }))} style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px',
                  background: '#fff', border: `2px solid ${selected ? '#2563eb' : '#e2e8f0'}`,
                  borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s',
                  boxShadow: selected ? '0 0 0 4px rgba(37,99,235,0.1)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>+</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>Cualquiera disponible</div>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 2 }}>Te asignamos al próximo disponible.</div>
                  </div>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${selected ? '#2563eb' : '#d1d5db'}`, background: selected ? '#2563eb' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                </div>
              );
            })()}
            {branchWorkers.map(w => {
              const selected = booking.worker?.id === w.id;
              const workerName = w.nombre || w.name;
              return (
                <div key={w.id} onClick={() => setBooking(b => ({ ...b, worker: w }))} style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px',
                  background: '#fff', border: `2px solid ${selected ? '#2563eb' : '#e2e8f0'}`,
                  borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s',
                  boxShadow: selected ? '0 0 0 4px rgba(37,99,235,0.1)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  {w.url_foto
                    ? <img src={w.url_foto} alt="" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: selected ? '2px solid #2563eb' : '2px solid #e2e8f0' }} />
                    : <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>✂️</div>
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>{workerName}</div>
                    <div style={{ fontSize: '0.78rem', color: '#f59e0b', marginTop: 2 }}>★ Barbero profesional</div>
                  </div>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${selected ? '#2563eb' : '#d1d5db'}`, background: selected ? '#2563eb' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── STEP 3: FECHA Y HORA ── */}
      {step === 3 && (
        <div>
          <h2 style={{ fontWeight: 800, fontSize: '1.3rem', color: '#0f172a', marginBottom: 4 }}>Fecha y hora</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 24 }}>Elige el día y la hora que prefieras.</p>

          {/* Week navigation */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <button onClick={() => goWeek(-1)} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#374151' }}>‹</button>
              <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', textTransform: 'capitalize' }}>
                {weekStart.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}
              </span>
              <button onClick={() => goWeek(1)} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#374151' }}>›</button>
            </div>
            {loadingAvail ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 6 }}>
                {weekDays.map((d, i) => {
                  const ds = toDS(d);
                  const isPast = ds < todayStr;
                  const hasSlots = availDates ? availDates.includes(ds) : !isPast;
                  const disabled = isPast || !hasSlots;
                  const isSelected = booking.date === ds;
                  const isToday = ds === todayStr;
                  return (
                    <div
                      key={i}
                      title={disabled ? (isPast ? 'Fecha ya pasada' : 'Día no disponible') : undefined}
                      onClick={() => { if (disabled) avisarDiaNoDisponible(ds, isPast); else { setDayMsg(''); selectDay(ds); } }}
                      style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      padding: '10px 4px', borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer',
                      background: isSelected ? '#2563eb' : isToday ? '#eff6ff' : disabled ? '#f8fafc' : '#f8fafc',
                      border: `2px solid ${isSelected ? '#2563eb' : isToday ? '#bfdbfe' : '#f1f5f9'}`,
                      opacity: disabled ? 0.4 : 1, transition: 'all 0.15s',
                    }}>
                      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: isSelected ? 'rgba(255,255,255,0.8)' : '#94a3b8', textTransform: 'uppercase' }}>{dayNames[i]}</span>
                      <span style={{ fontWeight: 800, fontSize: '1rem', color: isSelected ? '#fff' : isToday ? '#2563eb' : '#0f172a' }}>{d.getDate()}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {dayMsg && (
              <div role="alert" style={{
                marginTop: 14, padding: '10px 12px', borderRadius: 10,
                background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
                fontSize: '0.85rem', fontWeight: 600, textAlign: 'center',
              }}>
                ⚠️ {dayMsg}
              </div>
            )}
          </div>

          {/* Time slots */}
          {booking.date && (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', marginBottom: 14 }}>
                {new Date(booking.date + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
              {slotsLoading ? (
                <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
              ) : slots.length === 0 ? (
                <div style={{ padding: 16, background: '#fefce8', borderRadius: 10, color: '#a16207', fontSize: '0.85rem', textAlign: 'center' }}>
                  {slotsMsg || 'No hay horarios disponibles para este día.'}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px,1fr))', gap: 8 }}>
                  {slots.map(slot => {
                    const active = booking.time === slot;
                    return (
                      <div key={slot} onClick={() => setBooking(b => ({ ...b, time: slot }))} style={{
                        padding: '10px 6px', borderRadius: 10, textAlign: 'center', cursor: 'pointer',
                        border: `2px solid ${active ? '#2563eb' : '#e2e8f0'}`,
                        background: active ? '#2563eb' : '#f8fafc',
                        color: active ? '#fff' : '#374151',
                        fontWeight: active ? 700 : 500, fontSize: '0.85rem',
                        transition: 'all 0.15s',
                      }}>{to12h(slot)}</div>
                    );
                  })}
                </div>
              )}
              {booking.time && booking.service && (
                <div style={{ marginTop: 14, padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, fontSize: '0.82rem', color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                  ⏱ Duración total: {booking.service.duracion_minutos || booking.service.duration_minutes} minutos
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── STEP 4: DATOS ── */}
      {step === 4 && (
        <div>
          <h2 style={{ fontWeight: 800, fontSize: '1.3rem', color: '#0f172a', marginBottom: 4 }}>Tus datos</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 24 }}>Completa tus datos para agendar tu cita.</p>
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { icon: '👤', label: 'Nombre completo', key: 'client_name', type: 'text', placeholder: 'Andrés Pérez' },
              { icon: '📞', label: 'Teléfono', key: 'client_phone', type: 'tel', placeholder: '300 123 4567' },
            ].map(({ icon, label, key, type, placeholder }) => (
              <div key={key}>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 12, background: '#fff', transition: 'border 0.15s' }}
                  onFocus={() => {}} >
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
                  {key === 'client_phone' && (
                    <span style={{ fontSize: '0.95rem', color: '#64748b', fontWeight: 600, flexShrink: 0 }}>+57</span>
                  )}
                  <input type={type} value={booking[key]} placeholder={key === 'client_phone' ? '300 123 4567' : placeholder}
                    onChange={e => setBooking(b => ({ ...b, [key]: e.target.value }))}
                    style={{ border: 'none', outline: 'none', fontSize: '0.95rem', color: '#0f172a', background: 'transparent', width: '100%', fontFamily: 'inherit' }} />
                </div>
              </div>
            ))}
          </div>

          {/* Mini resumen */}
          <div style={{ marginTop: 20, background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>Resumen de tu cita</span>
              <button onClick={() => setStep(0)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#2563eb', fontWeight: 600, padding: 0 }}>Editar</button>
            </div>
            {[
              ['✂️ Servicio', `${booking.service?.nombre || booking.service?.name}`, `${booking.service?.duracion_minutos || booking.service?.duration_minutes} min`],
              ['👤 Barbero', booking.worker ? (booking.worker.nombre || booking.worker.name) : 'Cualquier disponible', null],
              ['📅 Fecha y hora', fmtDateShort(booking.date), to12h(booking.time)],
            ].map(([label, val, extra]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' }}>
                <span style={{ color: '#64748b' }}>{label}</span>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>{val}{extra ? <span style={{ color: '#94a3b8', fontWeight: 400 }}> · {extra}</span> : ''}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16, padding: '12px 16px', background: '#f0fdf4', borderRadius: 12, fontSize: '0.82rem', color: '#166534', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>🛡️</span>
            <div><strong>Pago en el establecimiento</strong><br/>Pagarás el día de tu cita.</div>
          </div>
        </div>
      )}

      {/* ── STEP 5: CONFIRMAR ── */}
      {step === 5 && (
        <div>
          <h2 style={{ fontWeight: 800, fontSize: '1.3rem', color: '#0f172a', marginBottom: 4 }}>Confirmar cita</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 24 }}>Revisa los detalles antes de confirmar.</p>
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
            {[
              ['👤 Cliente', booking.client_name],
              ['📞 Teléfono', booking.client_phone],
              ['✂️ Servicio', `${booking.service?.nombre || booking.service?.name} — ${fmt(booking.service?.precio || booking.service?.price)}`],
              ['👨‍💼 Barbero', booking.worker ? (booking.worker.nombre || booking.worker.name) : 'Cualquier disponible'],
              ['📅 Fecha', fmtDateLong(booking.date)],
              ['🕐 Hora', to12h(booking.time)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.88rem' }}>
                <span style={{ color: '#64748b' }}>{k}</span>
                <span style={{ fontWeight: 600, color: '#0f172a', textAlign: 'right', maxWidth: '55%' }}>{v}</span>
              </div>
            ))}
          </div>
          {error && <div style={{ padding: '12px 16px', background: '#fef2f2', borderRadius: 12, color: '#dc2626', fontSize: '0.85rem', marginBottom: 16 }}>{error}</div>}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Top bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={step > 0 ? prevStep : onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: '#374151', fontWeight: 600, fontSize: '0.95rem', padding: 0 }}>
          ← {step > 0 ? 'Atrás' : 'Inicio'}
        </button>
        <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>Agendar cita</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
          🔒 Pago seguro
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px' }}>
        <StepBar step={step} noBranches={noBranches} noWorkers={noWorkers} onGoTo={step < 5 ? goToStep : null} />
        <div className="bk-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 28, alignItems: 'start' }}>
          {/* Main */}
          <div>
            {mainContent}
            {/* Continue button */}
            {step < 5 && (
              <button
                onClick={nextStep}
                disabled={!canContinue()}
                style={{
                  width: '100%', marginTop: 24, padding: '16px', borderRadius: 14, border: 'none',
                  background: canContinue() ? '#2563eb' : '#e2e8f0',
                  color: canContinue() ? '#fff' : '#94a3b8',
                  fontWeight: 700, fontSize: '1rem', cursor: canContinue() ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s',
                }}>
                Continuar →
              </button>
            )}
            {step === 5 && (
              <button onClick={handleConfirm} disabled={submitting} style={{
                width: '100%', marginTop: 24, padding: '16px', borderRadius: 14, border: 'none',
                background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
                {submitting ? 'Confirmando...' : '✓ Confirmar cita'}
              </button>
            )}
            {/* Policy */}
            <div style={{ marginTop: 20, padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10, background: '#fff' }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>⏱</span>
              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                <strong style={{ color: '#374151' }}>Política de cancelación</strong><br/>
                Puedes cancelar o reagendar tu cita hasta 2 horas antes sin ningún costo.
              </div>
            </div>
          </div>
          {/* Sidebar */}
          <div className="bk-sidebar-col">
            <SummarySidebar booking={booking} step={step} onChangeStep={s => setStep(s)} />
          </div>
        </div>
      </div>

    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ HISTORY ═════ */
function HistoryScreen({ business, workers = [], slug, onBack }) {
  const [phone, setPhone]             = useState('');
  const [searched, setSearched]       = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rsWorker, setRsWorker]       = useState(null);
  const [rsYear, setRsYear]           = useState(new Date().getFullYear());
  const [rsMonth, setRsMonth]         = useState(new Date().getMonth());
  const [rsAvailDates, setRsAvailDates] = useState(null);
  const [rsLoadingMonth, setRsLoadingMonth] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleTime, setRescheduleTime]   = useState('');
  const [rsLoading, setRsLoading]     = useState(false);
  const [saving, setSaving]           = useState(false);

  const normalizeHistoryPhone = p => {
    const digits = p.replace(/\D/g, '');
    return digits.startsWith('57') ? digits : '57' + digits;
  };

  const search = async () => {
    if (!phone.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await api.get('/appointments/public/client', { params: { phone: normalizeHistoryPhone(phone.trim()), business_id: business.id } });
      setAppointments(res.data); setSearched(true);
    } catch { setError('Error al consultar. Verifica tu número.'); }
    finally { setLoading(false); }
  };

  const cancel = async appt => {
    if (!window.confirm('¿Cancelar esta cita?')) return;
    try {
      await api.patch(`/appointments/public/${appt.id}/cancel`, { phone: normalizeHistoryPhone(phone.trim()), reason: 'Cancelada por el cliente' });
      search();
    } catch (err) { setError(err.response?.data?.error || 'Error al cancelar'); }
  };

  const openReschedule = appt => {
    setRescheduleAppt(appt);
    setRsWorker(appt.trabajador_id ? { id: appt.trabajador_id, nombre: appt.barbero } : null);
    setRsYear(new Date().getFullYear()); setRsMonth(new Date().getMonth());
    setRsAvailDates(null); setRescheduleDate(''); setRescheduleTime(''); setRescheduleSlots([]);
  };

  const fetchRsMonthAvail = async (year, month, workerId, serviceId) => {
    if (!serviceId) return;
    setRsLoadingMonth(true); setRsAvailDates(null);
    try {
      const res = await api.get('/appointments/availability/month', {
        params: { business_slug: slug, worker_id: workerId || '', year, month: month + 1, service_id: serviceId }
      });
      setRsAvailDates(res.data.available || []);
    } catch { setRsAvailDates([]); }
    finally { setRsLoadingMonth(false); }
  };

  const fetchRescheduleSlots = async (date, workerId, serviceId) => {
    if (!date || !serviceId) return;
    setRsLoading(true);
    try {
      const res = await api.get('/appointments/availability', {
        params: { business_slug: slug, worker_id: workerId || '', date, service_id: serviceId }
      });
      setRescheduleSlots(res.data.slots || []);
    } catch { setRescheduleSlots([]); }
    finally { setRsLoading(false); }
  };

  const confirmReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) return;
    setSaving(true);
    try {
      await api.patch(`/appointments/public/${rescheduleAppt.id}/reschedule`, {
        phone: normalizeHistoryPhone(phone.trim()), new_date: rescheduleDate, new_time: rescheduleTime, new_worker_id: rsWorker?.id || null,
      });
      setRescheduleAppt(null); search();
    } catch (err) { setError(err.response?.data?.error || 'Error al reagendar'); }
    finally { setSaving(false); }
  };

  const canModify = appt => appt.estado === 'confirmed' && appt.fecha_cita >= todayStr;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16, position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#374151', fontWeight: 600, fontSize: '0.95rem', padding: 0 }}>← Inicio</button>
        <span style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>Mis citas</span>
      </div>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '28px 20px' }}>
        <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 20 }}>Ingresa tu celular para ver tus citas.</p>
        {error && <div style={{ padding: '12px 16px', background: '#fef2f2', borderRadius: 12, color: '#dc2626', fontSize: '0.85rem', marginBottom: 16 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff', padding: '0 12px' }}>
            <span style={{ fontSize: '0.95rem', color: '#64748b', fontWeight: 600, flexShrink: 0, marginRight: 6 }}>+57</span>
            <input
              type="tel"
              placeholder="300 123 4567"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && search()}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.95rem', color: '#0f172a', background: 'transparent', padding: '10px 0', fontFamily: 'inherit' }}
            />
          </div>
          <button onClick={search} disabled={loading || !phone.trim()} style={{
            padding: '10px 20px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem',
          }}>{loading ? '...' : 'Buscar'}</button>
        </div>

        {searched && appointments.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>Sin citas</div>
            <div style={{ fontSize: '0.85rem' }}>No encontramos citas con este número.</div>
          </div>
        )}

        {appointments.map(appt => (
          <div key={appt.id} style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', marginBottom: 4 }}>{appt.servicio}</div>
                <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                  📅 {fmtDateShort(appt.fecha_cita)} · {to12h(appt.hora_inicio)}
                </div>
                {appt.barbero && <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 2 }}>✂️ {appt.barbero}</div>}
              </div>
              <span className={`badge badge-${appt.estado || 'confirmed'}`} style={{ flexShrink: 0 }}>
                {STATUS_LABEL[appt.estado] || appt.estado}
              </span>
            </div>
            {canModify(appt) && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => openReschedule(appt)} style={{ flex: 1, padding: '9px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                  📅 Reagendar
                </button>
                <button onClick={() => cancel(appt)} style={{ flex: 1, padding: '9px', borderRadius: 10, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                  ✕ Cancelar
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Reagendar modal */}
      {rescheduleAppt && (() => {
        const svcId = rescheduleAppt.servicio_id || rescheduleAppt.id;
        const rsDays = (() => {
          const first = new Date(rsYear, rsMonth, 1).getDay();
          const total = new Date(rsYear, rsMonth + 1, 0).getDate();
          const arr = []; for (let i = 0; i < first; i++) arr.push(null);
          for (let i = 1; i <= total; i++) arr.push(i); return arr;
        })();
        if (!rsLoadingMonth && rsAvailDates === null) fetchRsMonthAvail(rsYear, rsMonth, rsWorker?.id, svcId);
        const goRsMonth = dir => {
          let y = rsYear, m = rsMonth + dir;
          if (m < 0) { y--; m = 11; } else if (m > 11) { y++; m = 0; }
          setRsYear(y); setRsMonth(m); setRsAvailDates(null); setRescheduleDate(''); setRescheduleTime('');
          fetchRsMonthAvail(y, m, rsWorker?.id, svcId);
        };
        return (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: 480 }}>
              <div className="modal-header">
                <span className="modal-title">Reagendar cita</span>
                <button className="btn btn-ghost btn-icon" onClick={() => setRescheduleAppt(null)}>✕</button>
              </div>
              <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                <p style={{ marginBottom: 14, fontSize: '0.82rem', color: '#64748b' }}>
                  Cita actual: <strong>{fmtDateShort(rescheduleAppt.fecha_cita)}</strong> · <strong>{to12h(rescheduleAppt.hora_inicio)}</strong>
                </p>
                <div className="form-group">
                  <label className="form-label">Barbero</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div className={`service-card ${!rsWorker ? 'active' : ''}`} style={{ padding: '10px 14px' }}
                      onClick={() => { setRsWorker(null); setRsAvailDates(null); setRescheduleDate(''); setRescheduleTime(''); fetchRsMonthAvail(rsYear, rsMonth, null, svcId); }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>✂️ Sin preferencia</span>
                    </div>
                    {workers.map(w => (
                      <div key={w.id} className={`service-card ${rsWorker?.id === w.id ? 'active' : ''}`} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}
                        onClick={() => { setRsWorker(w); setRsAvailDates(null); setRescheduleDate(''); setRescheduleTime(''); fetchRsMonthAvail(rsYear, rsMonth, w.id, svcId); }}>
                        {w.url_foto ? <img src={w.url_foto} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} /> : <span style={{ fontSize: 18 }}>✂️</span>}
                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{w.nombre || w.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Nueva fecha</label>
                  <div className="card" style={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => goRsMonth(-1)}>◀</button>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{new Date(rsYear, rsMonth).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}</span>
                      <button className="btn btn-ghost btn-icon" onClick={() => goRsMonth(1)}>▶</button>
                    </div>
                    {rsLoadingMonth ? <div style={{ textAlign: 'center', padding: 16 }}><div className="spinner" style={{ margin: '0 auto' }} /></div> : (
                      <div className="calendar-grid">
                        {['D','L','M','X','J','V','S'].map(d => <div key={d} style={{ textAlign: 'center', fontSize: '0.65rem', color: '#94a3b8', padding: 2 }}>{d}</div>)}
                        {rsDays.map((d, i) => {
                          if (!d) return <div key={i} />;
                          const ds = `${rsYear}-${String(rsMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                          const isPast = ds < todayStr;
                          const hasSlots = rsAvailDates ? rsAvailDates.includes(ds) : true;
                          const disabled = isPast || !hasSlots;
                          return (
                            <div key={i} className={`cal-day ${rescheduleDate === ds ? 'selected' : ''}`}
                              style={{ opacity: disabled ? 0.25 : 1, cursor: disabled ? 'not-allowed' : 'pointer', textDecoration: !hasSlots && !isPast ? 'line-through' : 'none' }}
                              onClick={() => { if (disabled) return; setRescheduleDate(ds); setRescheduleTime(''); fetchRescheduleSlots(ds, rsWorker?.id, svcId); }}>
                              {d}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {rescheduleDate && (
                  <div className="form-group">
                    <label className="form-label">Nueva hora</label>
                    {rsLoading ? <div style={{ textAlign: 'center', padding: 16 }}><div className="spinner" style={{ margin: '0 auto' }} /></div> :
                      rescheduleSlots.length === 0 ? <div className="alert alert-warning">No hay horarios disponibles.</div> : (
                        <div className="time-grid">
                          {rescheduleSlots.map(s => (
                            <div key={s} className={`time-slot ${rescheduleTime === s ? 'active' : ''}`} onClick={() => setRescheduleTime(s)}>{to12h(s)}</div>
                          ))}
                        </div>
                      )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setRescheduleAppt(null)}>Cancelar</button>
                <button className="btn btn-primary" disabled={!rescheduleDate || !rescheduleTime || saving} onClick={confirmReschedule}>
                  {saving ? 'Guardando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
